import React, { useContext, useRef, useState } from "react";
import {
  BackIcon,
  DarkModeIcon,
  EmailIcon,
  LightModeIcon,
} from "../../components/iconSet/IconSet";
import { ThemeTypes } from "../../context/ContextTypes";

import logoIcon from "../../images/icons/logo.png";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../context/ThemeContext";
import { generatePKCE } from "../../utils/keycloakPKCEUtils";
import AlertDialog from "../../components/popupComp/AlertDialog";
import axios from "axios";
import "./ForgotPasswordNew2.css";

const ForgotPasswordNew2 = () => {
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const emailRef = useRef<HTMLInputElement | null>(null);

  const [alertDialogState, setAlertDialogState] = useState(false);
  const [alertTitle, setAlertTitle] = useState("");
  const [alertDescription, setAlertDescription] = useState("");
  const [alertYesCallback, setAlertYesCallback] = useState<(() => void) | undefined>(undefined);
  const [alertOkCallback, setAlertOkCallback] = useState<(() => void) | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [error, setError] = useState<string>('')
  const [success, setSuccess] = useState<string>('')

  const showAlertDialog = (
    title: string,
    description: string,
    yesCallback?: () => void,
    okCallback?: () => void
  ) => {
    setAlertTitle(title);
    setAlertDescription(description);
    setAlertYesCallback(() => yesCallback);
    setAlertOkCallback(() => okCallback);
    setAlertDialogState(true);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const inputValue = emailRef.current?.value.trim();
    if (!inputValue) {
      showAlertDialog("Validation Error", "❗ Please enter your email.");
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await axios.post(
        `${BASE_URL}/jwt-auth/api/v1/forgot-password`,
        { email: inputValue },
        { headers: { "Content-Type": "application/json" } }
      );

      /* showAlertDialog(
        "✅ Success",
        "Password reset email sent successfully.",
        undefined,
        () => window.location.reload()
      ); */
      setSuccess("Password reset email sent successfully.");
      window.location.reload()
    } catch (error: any) {
      const message =
        error.response?.data?.error ||
        "Something went wrong. Please try again.";
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleThemeChangeClick = (type: ThemeTypes) => {
    setTheme((prevState) => type);
  };

  return (
    <div className="forgot-password-root">
      {/* Background Video */}
      <video
        autoPlay
        muted
        playsInline
        className="background-video"
        loop
      >
        <source src="/MV_FVM_Video.mp4" type="video/mp4" />
      </video>
      <div className="video-overlay" />

      {/* Main Content */}
      <div className="forgot-password-container">
        <div className="forgot-password-card">
          {/* Brand Header */}
          <div className="brand-area">
            <img src={logoIcon} alt="BCONX" className="brand-logo" />
            <h1>BCONX</h1>
            <p className="brand-subtitle">
              Backoffice System
            </p>
          </div>

          {/* Form Section */}
          <form onSubmit={handleForgotPassword} className="forgot-form">
            <div className="form-header">
              <h2>Forgot Password?</h2>
              <p className="form-description">
                Enter your registered email address and we'll send you a link to reset your password.
              </p>
            </div>

            <div className="inputGroup">
              <label htmlFor="email">Email Address</label>
              <div className="input-wrapper">
                <span className="input-icon">
                  <EmailIcon color="rgba(255,255,255,0.6)" width="18px" />
                </span>
                <input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  ref={emailRef}
                  required
                  autoComplete="email"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {error && (
                <div className={'errorMessage'} role="alert">
                    {error}
                </div>
            )}

            {success && (
                <div className={'successMessage'} role="status">
                    {success}
                </div>
            )}

            <button
              type="submit"
              className="submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Sending..." : "Send Reset Link"}
            </button>
          </form>

          {/* Back to Login */}
          <button
            type="button"
            className="back-to-login"
            onClick={() => navigate("/login")}
            disabled={isSubmitting}
          >
            <BackIcon color="rgba(255,255,255,0.8)" width="16px" />
            <span>Back to Login</span>
          </button>
        </div>
      </div>

      {/* Alert Dialog */}
      {alertDialogState && (
        <AlertDialog
          title={alertTitle}
          description={alertDescription}
          windowState={alertDialogState}
          setWindowState={setAlertDialogState}
          function={alertYesCallback}
          okFunction={alertOkCallback}
        />
      )}
    </div>
  );
};

export default ForgotPasswordNew2;